/**
 * Running the duplicate scan.
 *
 * Everything here is a consequence of the server holding no key. It cannot
 * compare two photographs, so the browser has to fetch every thumbnail, decrypt
 * it, and hash it — which is slow enough to need a progress bar and heavy
 * enough to need a limit on how much is in flight at once.
 *
 * Thumbnails, not originals. A difference hash reads only large-scale structure
 * and throws away everything finer than one part in eight, so a 400-pixel
 * thumbnail and a 40-megapixel original produce the same answer. Using
 * originals would move gigabytes to learn nothing.
 */

import { useCallback, useRef, useState } from "react";
import { acquireDecryptedUrl, release } from "../../lib/mediaCache.js";
import type { MediaRef } from "../../lib/useDecryptedUrl.js";
import {
  type Hashed,
  type RelatedSet,
  chromaSignature,
  groupRelated,
  measurePixels,
} from "./phash.js";

export interface ScannableItem {
  id: string;
  kind: "photo" | "video";
  takenAt: number;
}

export type ScanPhase = "idle" | "listing" | "hashing" | "done";

export interface ScanState {
  phase: ScanPhase;
  hashed: number;
  total: number;
  sets: RelatedSet[];
  /** Files whose thumbnail could not be read. Reported, never silently dropped. */
  unreadable: number;
}

/*
 * The grid the hash is taken from is 9×8. Drawing straight to that would let
 * the browser's own resampler do the averaging, but at that ratio its filter
 * quality varies between engines and the hash would differ between browsers.
 * A 32×32 intermediate is small enough to be cheap and large enough that the
 * box filter afterwards is doing the work, identically everywhere.
 */
const WORK_SIZE = 32;

/** Decrypts in flight at once. Above this a phone runs out of memory. */
const CONCURRENCY = 4;

export function useDuplicateScan(groupId: string) {
  const [state, setState] = useState<ScanState>({
    phase: "idle",
    hashed: 0,
    total: 0,
    sets: [],
    unreadable: 0,
  });

  const cancelled = useRef(false);

  const scan = useCallback(
    async (
      items: ScannableItem[],
      thumbRefs: ReadonlyMap<string, MediaRef>,
      loadEverything: () => Promise<void>,
    ) => {
      cancelled.current = false;
      setState({
        phase: "listing",
        hashed: 0,
        total: 0,
        sets: [],
        unreadable: 0,
      });

      // A scan of half a library would report duplicates that are not there and
      // miss the ones that are, so every page is pulled in first.
      await loadEverything();
      if (cancelled.current) return;

      const photos = items.filter((item) => item.kind === "photo");
      setState((current) => ({
        ...current,
        phase: "hashing",
        total: photos.length,
      }));

      const canvas = document.createElement("canvas");
      canvas.width = WORK_SIZE;
      canvas.height = WORK_SIZE;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        setState((current) => ({ ...current, phase: "done" }));
        return;
      }

      const hashed: Hashed[] = [];
      let unreadable = 0;
      let next = 0;

      const worker = async () => {
        for (;;) {
          if (cancelled.current) return;
          const index = next++;
          const item = photos[index];
          if (!item) return;

          const ref = thumbRefs.get(item.id);
          if (!ref) {
            unreadable++;
            continue;
          }

          let objectUrl: string | null = null;
          try {
            objectUrl = await acquireDecryptedUrl({
              url: ref.url,
              wrappedFileKey: ref.wrappedFileKey,
              groupId,
            });
            const blob = await (await fetch(objectUrl)).blob();
            const bitmap = await createImageBitmap(blob);
            try {
              /*
               * Drawn without preserving aspect, deliberately. The hash is a
               * grid of comparisons, so it has to be taken over the same grid
               * every time; letterboxing would put bars of flat colour into
               * the frame and every portrait photograph would hash alike.
               */
              context.drawImage(bitmap, 0, 0, WORK_SIZE, WORK_SIZE);
              const pixels = context.getImageData(
                0,
                0,
                WORK_SIZE,
                WORK_SIZE,
              ).data;
              const measured = measurePixels(pixels, WORK_SIZE, WORK_SIZE);
              hashed.push({
                id: item.id,
                hash: measured.hash,
                structure: measured.structure,
                chroma: chromaSignature(pixels, WORK_SIZE, WORK_SIZE),
                takenAt: item.takenAt,
              });
            } finally {
              bitmap.close();
            }
          } catch {
            unreadable++;
          } finally {
            if (objectUrl) release(objectUrl);
          }

          setState((current) => ({ ...current, hashed: current.hashed + 1 }));
        }
      };

      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
      if (cancelled.current) return;

      setState({
        phase: "done",
        hashed: hashed.length,
        total: photos.length,
        sets: groupRelated(hashed),
        unreadable,
      });
    },
    [groupId],
  );

  const reset = useCallback(() => {
    cancelled.current = true;
    setState({ phase: "idle", hashed: 0, total: 0, sets: [], unreadable: 0 });
  }, []);

  return { state, scan, reset };
}
